import { unwrapApiData } from '@/shared/utils/apiResponse';
import { normalizeCpfCnpj } from '@/shared/utils/documentValidation';
import { apiGet, apiPost } from '@/shared/utils/requestBuilder';

import { TERMOS_VERSAO } from '../constants';
import type {
  CadastroForm,
  CadastroResultado,
  ConsultaCnpjResultado,
  ConsultaPagamentoResultado,
  PagamentoResultado,
  PlanoApi,
} from '../types';

const SKIP_AUTH = true;

const unwrap = <T>(response: Response, fallback: string): Promise<T> =>
  unwrapApiData<T>(response, { fallback, source: 'notificacaoSelf.services' });

export const notificacaoSelfService = {
  async carregarPlanos(): Promise<PlanoApi[]> {
    const response = await apiGet('/notificacao-self/planos', undefined, SKIP_AUTH);
    const data = await unwrap<{ planos: PlanoApi[] }>(response, 'Erro ao carregar os planos');
    return data.planos;
  },

  async consultarCnpj(cnpj: string, signal?: AbortSignal): Promise<ConsultaCnpjResultado> {
    const response = await apiPost(
      '/notificacao-self/cnpj',
      { cnpj: normalizeCpfCnpj(cnpj) },
      SKIP_AUTH,
      signal,
    );
    return unwrap<ConsultaCnpjResultado>(response, 'Erro ao consultar o CNPJ');
  },

  async enviarCadastro(
    form: CadastroForm,
    variationId: string,
    signal?: AbortSignal,
  ): Promise<CadastroResultado> {
    const response = await apiPost(
      '/notificacao-self/leads',
      {
        cnpj: normalizeCpfCnpj(form.cnpj),
        razaoSocial: form.razaoSocial,
        nomeFantasia: form.nomeFantasia,
        telefone: form.telefone,
        responsavelNome: form.responsavelNome,
        responsavelEmail: form.responsavelEmail,
        variationId,
        aceiteTermos: form.termosAceitos,
        termosVersao: TERMOS_VERSAO,
      },
      SKIP_AUTH,
      signal,
    );
    return unwrap<CadastroResultado>(response, 'Erro ao enviar o cadastro');
  },

  async iniciarPagamento(
    payload: { leadId: string; accessToken: string },
    signal?: AbortSignal,
  ): Promise<PagamentoResultado> {
    const response = await apiPost(
      `/notificacao-self/leads/${payload.leadId}/pagamento`,
      { accessToken: payload.accessToken },
      SKIP_AUTH,
      signal,
    );
    return unwrap<PagamentoResultado>(response, 'Erro ao iniciar o pagamento');
  },

  async criarSessao(payload: { leadId: string; accessToken: string }): Promise<void> {
    const response = await apiPost(
      `/notificacao-self/leads/${payload.leadId}/sessao`,
      { accessToken: payload.accessToken },
      SKIP_AUTH,
    );
    if (!response.ok) {
      throw new Error('Não foi possível criar a sessão de primeiro acesso');
    }
  },

  async consultarPagamento(
    payload: { leadId: string; accessToken: string },
    signal?: AbortSignal,
  ): Promise<ConsultaPagamentoResultado> {
    const response = await apiPost(
      `/notificacao-self/leads/${payload.leadId}/pagamento/consulta`,
      { accessToken: payload.accessToken },
      SKIP_AUTH,
      signal,
    );
    return unwrap<ConsultaPagamentoResultado>(response, 'Erro ao consultar o pagamento');
  },
};
